import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PointsApiProviderService } from '@core/api';
import { stringifyAlphabeticalObject } from '@core/helpers';
import { TLayoutConfig } from 'react-lib/components/organisms/CustomFieldGroupsSettings';
import { BehaviorSubject, catchError, map, Observable, of, Subject, takeUntil, tap } from 'rxjs';
import { CustomFieldsLayoutApiProviderService } from 'src/app/project/data-providers/api-providers/custom-fields-provider/custom-fields-provider';
import { NEW_POINT_ID } from 'src/app/project/shared/constants/point.const';
import { ELocalStorageItems } from 'src/app/project/shared/enums/local-storage-items.enum';
import { compareCustomFieldColumns } from '../../../custom-fields/combine-custom-fields/compare-custom-fields';
import { TCustomField } from '../../../custom-fields/custom-fields.model';
import { flattenFieldsLayout } from '../../../custom-fields/utils/flatten-fields-layout';
import { logErrorInSentry } from '../../../errors/response-error';
import { QuickFilterService } from '../../../filters/filters-api/search/quick-filter-service';
import { QuickSortService } from '../../../filters/filters-api/sort/quick-sort.service';
import {
  TPointCustomFieldAllSumsResponse,
  TPointCustomFieldQuery,
  TPointCustomFieldQueryGroup,
} from '../../../points/points-custom-fields-sum/points-custom-fields-sum.const';
import { notifyGroupSizesChange } from '../../../points/points-group-sizes/group-sizes.store';
import { TGroupSizesResponse } from '../../../points/points-group-sizes/points-group-sizes.consts';
import { checkCustomWorkspaceId } from '../../../workspace/workspace';
import { EWorkspaces } from '../../../workspace/workspaces.enum';
import { GET_TIMELINE } from '../../site-timeline/timeline.ui.store';
import { TColumn } from '../columns/column.model';
import { generateDefaultColumns, getDefaultWidth } from '../columns/columns';
import { EDefaultColumnWidths } from '../columns/default-column-widths-enum';
import { getAllGroupedSortedColumns } from '../columns/get-all-grouped-sorted-columns';
import { TableColumnsService } from '../columns/table-columns.service';
import { findPropertyPath } from '../properties';
import { GET_TABLE } from '../table.ui.store';
import { CLEAR_CF_SUM, SET_CF_SUM } from './custom-field-sums.store';
import Table from './table/Table';
import { checkColumnsToAdd } from './table/utils/check-columns-to-add';
import { checkColumnsToDelete } from './table/utils/check-columns-to-delete';
import { findLayoutInsertIndex } from './table/utils/find-layout-insert-index';
import { updatePointCustomFields } from './table/utils/update-point-custom-field';

const MINUTES_5_IN_MS = 5 * 60 * 1000;
const COLUMN_SUMS_CACHE_TTL_MS = MINUTES_5_IN_MS;

type TColumnSumsCacheEntry = {
  savedAt: number;
  data: TPointCustomFieldAllSumsResponse;
};

@Injectable({
  providedIn: 'root',
})
export class CustomTableService {
  isOverview: boolean;
  groupTotals: TGroupSizesResponse | undefined;

  private readonly _columnsHaveUpdated$ = new BehaviorSubject<boolean>(false);
  readonly columnsHaveUpdated$ = this._columnsHaveUpdated$.asObservable();

  private readonly cancelGroupSizesFetch$ = new Subject<void>();

  private readonly columnSumsCache = new Map<string, TColumnSumsCacheEntry>();

  private readonly fieldsLayoutCache = new Map<string, TLayoutConfig[]>();

  constructor(
    private tableColumnsService: TableColumnsService,
    private pointsApiProviderService: PointsApiProviderService,
    private quickFilterService: QuickFilterService,
    private quickSortService: QuickSortService,
    private customFieldsLayoutApiProviderService: CustomFieldsLayoutApiProviderService,
  ) {}

  clearFieldsLayout(): void {
    this.fieldsLayoutCache.clear();
  }

  fetchFieldsLayout(workspaceId: string): Observable<TLayoutConfig[]> {
    return this.customFieldsLayoutApiProviderService.getAllCustomFieldsLayout(workspaceId).pipe(
      map((response) => response.fieldsLayout),
      tap((fieldsLayout) => this.fieldsLayoutCache.set(workspaceId, fieldsLayout)),
      catchError((error) => {
        logErrorInSentry({ error, message: 'Error fetching custom field layout on site table' });

        return of([]);
      }),
    );
  }

  hasUpdatedColumns(): void {
    this._columnsHaveUpdated$.next(true);
  }

  getTable(): Table {
    return GET_TABLE();
  }

  clearSavedColumnSums(): void {
    this.columnSumsCache.clear();
    CLEAR_CF_SUM();
  }

  clearGroupSizes(): void {
    localStorage.removeItem(ELocalStorageItems.GROUP_SIZES);
    this.groupTotals = undefined;
  }

  checkColumns(
    customFields: TCustomField[],
    workspaceId: string,
    updatePreferences: boolean = true,
  ): void {
    const table = GET_TABLE();

    if (!table || !customFields) {
      return;
    }

    const oldColumns = this.tableColumnsService.getColumns();
    const { columns, columnsChanged } = this.checkColumnsChanged(
      oldColumns,
      customFields,
      workspaceId,
    );

    this.tableColumnsService.setColumns(columns);

    table.tableHead.updateWidth();
    table.tableBody.updateWidth();
    table.tableFooter.updateWidth();
    table.tableBody.updateColumns();

    if (columnsChanged && !table.defaultColumnsLoaded && updatePreferences) {
      table.savePreferencesCallback();
    }
  }

  generateDefaultSiteColumns(customFields: TCustomField[], workspaceId: string): TColumn[] {
    const columnsToCheck = this.resetColumnData();
    const oldColumns = columnsToCheck;

    if (customFields) {
      const { columns } = this.checkColumnsChanged(oldColumns, customFields, workspaceId);

      return columns;
    }

    return oldColumns;
  }

  updatePoint({
    _id,
    field,
    newValue = null,
    updatedDate = null,
  }: {
    _id: string;
    field: string;
    newValue?: unknown;
    updatedDate?: number;
  }): void {
    const columns = this.tableColumnsService.getColumns();
    const table = GET_TABLE();

    if (table) {
      const pointIndex = table.getPoints().findIndex((point) => point._id === _id);

      if (pointIndex > -1 && _id !== NEW_POINT_ID) {
        this.updatePointField(pointIndex, field, newValue);

        if (updatedDate) {
          this.updatePointUpdatedDate(pointIndex, updatedDate);
        }
      }

      if (pointIndex > -1) {
        const columnIndex = columns.findIndex((column) => column.name === field);

        table.tableBody.updatePoint(pointIndex, columnIndex);

        if (updatedDate) {
          const updatedColumnIndex = columns.findIndex((column) => column.name === 'Updated');

          table.tableBody.updatePoint(pointIndex, updatedColumnIndex);
        }
      }
    }
  }

  reloadTable(
    { reset = false, preserveTableState }: { reset?: boolean; preserveTableState?: boolean } = {
      reset: true,
      preserveTableState: false,
    },
  ): void {
    const table = GET_TABLE();

    this.isOverview = checkCustomWorkspaceId() === EWorkspaces.OVERVIEW;
    const isTimeline = checkCustomWorkspaceId() === EWorkspaces.TIMELINE;

    if (isTimeline) {
      const timeline = GET_TIMELINE();

      if (timeline) {
        timeline.reload({ reset, preserveTableState, timeout: false });
      }
    } else if (table) {
      const isOverview = checkCustomWorkspaceId() === EWorkspaces.OVERVIEW;

      table.reload({ reset, isOverview, preserveTableState });
    }
  }

  private updatePointField(pointIndex: number, field: string, newValue: unknown): void {
    const table = GET_TABLE();

    let propertyName = findPropertyPath(field);
    let isCustomField = false;

    if (!propertyName) {
      propertyName = field;
      isCustomField = true;
    }

    if (isCustomField) {
      updatePointCustomFields(pointIndex, field, newValue, table.getPoints());
    }
  }

  private updatePointUpdatedDate(pointIndex: number, newValue: number): void {
    const table = GET_TABLE();

    table.getPoints()[pointIndex].header.updatedEpochMillis = newValue;

    table.sortTable();
  }

  checkColumnsChanged(
    columns: TColumn[],
    customFields: TCustomField[],
    workspaceId: string,
  ): { columns: TColumn[]; columnsChanged: number } {
    const table = this.getTable();
    const { columnsToHide, columnsToShow } = table.checkColumnVisibility();

    const columnsToAdd = checkColumnsToAdd({
      customFields,
      isOverview: table.overview,
      existingColumns: columns,
    });
    const columnsToDelete = checkColumnsToDelete(customFields, table.overview);
    const layoutFieldIds = this.getLayoutFieldIds(workspaceId, table.overview);

    columnsToAdd.forEach((customField) => {
      const newColumn: TColumn = {
        name: customField.name,
        index: columns.length,
        width: EDefaultColumnWidths.CUSTOM_FIELD,
        showTotal: customField.showTotal || typeof customField.showTotal === 'undefined',
        customFieldIds: customField.customFieldIds,
        unit: customField.unit,
        decimalPlaces: customField.decimalPlaces,
        currency: customField.currency,
        subValuesActive: !!customField.subValuesActive,
        hidden: customField.hidden ?? false,
      };

      const existingColumn = columns.find((column) =>
        compareCustomFieldColumns(newColumn, column, workspaceId),
      );

      if (!existingColumn) {
        const insertIndex = findLayoutInsertIndex({ columns, newColumn, layoutFieldIds });

        table.visibleColumns.push(newColumn);
        columns.splice(insertIndex, 0, newColumn);
        table.width += newColumn.width;
      } else {
        existingColumn.customFieldIds.push(customField.customFieldIds[0]);
        table.defaultColumnsLoaded = false;
      }
    });

    columnsToDelete.forEach((column) => {
      const columnIndex = columns.findIndex((columnToDelete) => columnToDelete.name === column);

      if (columnIndex !== -1) {
        table.width -= columns[columnIndex].width;

        table.tableHead.removeColumn();
        columns.splice(columnIndex, 1);
      }
    });

    columns.forEach((_column, _index) => {
      _column.index = _index;
    });

    return {
      columns,
      columnsChanged:
        columnsToAdd.length ||
        columnsToDelete.length ||
        columnsToHide.length ||
        columnsToShow.length,
    };
  }

  private getLayoutFieldIds(workspaceId: string, isOverview: boolean): string[] {
    if (isOverview) {
      return [];
    }

    return flattenFieldsLayout(this.fieldsLayoutCache.get(workspaceId) ?? []);
  }

  resetColumnData(): TColumn[] {
    const columns = generateDefaultColumns();

    columns.forEach((column) => {
      column.width = getDefaultWidth(column.name);
      delete column.groupIndex;
      delete column.groupOrder;
      delete column.sortIndex;
      delete column.sortOrder;
      delete column.customFieldIds;
      column.hidden = false;
    });

    return columns;
  }

  fetchColumnSums(
    columns: TPointCustomFieldQueryGroup[],
    options: { invalidateCache?: boolean } = {},
  ): void {
    if (options.invalidateCache) {
      this.columnSumsCache.clear();
    }

    let query: TPointCustomFieldQuery = {
      groups: columns,
      sort: undefined,
      filter: this.quickFilterService.getFiltersTransformed(),
    };

    if (getAllGroupedSortedColumns().length > 0) {
      query = { ...query, sort: this.quickSortService.getSortTransformed() };
    }

    const cacheKey = stringifyAlphabeticalObject(query);
    const cached = this.readColumnSumsCache(cacheKey);

    if (cached) {
      this.hasUpdatedColumns();
      SET_CF_SUM(cached);

      const table = this.getTable();
      if (table) {
        table.tableFooter.load(true);
      }
      return;
    }

    const response = this.pointsApiProviderService.fetchCustomFieldSum(query);

    response.subscribe({
      next: (sumResponse: TPointCustomFieldAllSumsResponse) => {
        this.hasUpdatedColumns();
        this.columnSumsCache.set(cacheKey, { savedAt: Date.now(), data: sumResponse });
        SET_CF_SUM(sumResponse);

        const table = this.getTable();

        if (table) {
          table.tableFooter.load(true);
        }
      },
      error: (err: HttpErrorResponse) => {
        logErrorInSentry({
          message: `Error Fetching Column Sums: ${err?.message ?? `HTTP ${err?.status ?? 'unknown'}`}`,
          status: err?.status,
          error: err,
        });
      },
    });
  }

  private readColumnSumsCache(key: string): TPointCustomFieldAllSumsResponse | undefined {
    const entry = this.columnSumsCache.get(key);
    if (!entry) {
      return undefined;
    }
    if (Date.now() - entry.savedAt > COLUMN_SUMS_CACHE_TTL_MS) {
      this.columnSumsCache.delete(key);
      return undefined;
    }
    return entry.data;
  }

  fetchGroupSizes(columns: TPointCustomFieldQueryGroup[], workspaceId: string): void {
    const query: TPointCustomFieldQuery = {
      groups: columns,
      sort: this.quickSortService.getGroupingSortTransformed(),
      filter: this.quickFilterService.getFiltersTransformed(),
    };

    this.cancelGroupSizesFetch$.next();

    this.clearGroupSizes();
    notifyGroupSizesChange();

    const response = this.pointsApiProviderService
      .fetchGroupSizes(query, workspaceId)
      .pipe(takeUntil(this.cancelGroupSizesFetch$));

    response.subscribe({
      next: (sumResponse: TGroupSizesResponse) => {
        this.hasUpdatedColumns();
        this.groupTotals = sumResponse;
        localStorage.setItem(ELocalStorageItems.GROUP_SIZES, JSON.stringify(this.groupTotals));
        notifyGroupSizesChange();
      },
      error: (err: HttpErrorResponse) => {
        logErrorInSentry({
          message: `Error Fetching Group Sizes: ${err?.message ?? `HTTP ${err?.status ?? 'unknown'}`}`,
          status: err?.status,
          error: err,
        });
      },
    });
  }
}
